import { Button } from "components/buttons/button/Button";
import { type FormikProps, withFormik } from "formik";
import { difference, isEmpty, isNumber, some, uniq } from "lodash";
import { Component } from "react";
import { connect, type ConnectedProps } from "react-redux";
import { Navigate, Route, Routes } from "react-router";
import { bindActionCreators } from "redux";

import dataService from "../../../Application/services/dataServices/dataService";
import groupsDataService from "../../services/groupsDataService";
import rolesDataService from "../../services/rolesDataService";

import { withRouter, type WithRouterProps } from "adapters/withRouter/withRouter";
import EditUserPacksIssueLicenseModalContainer from "components/assignmentModals/peopleIssueLicenseModal/EditUserIssueLicenseModalContainer/EditUserPacksIssueLicenseModalContainer";
import RemovePriorityButton from "components/buttons/linkButtons/RemovePriorityButton/RemovePriorityButton";
import { type SelectedItem } from "components/contentAssignment/types";
import RemoveLicenseConfirmationDialog from "components/packs/removeLicenseConfirmationDialog/RemoveLicenseConfirmationDialog";
import { FeatureFlags } from "featureFlags";
import { reset } from "features/Licensing/ContentAssignmentModalLicensingSteps/state/slices/licensingModalSlice";
import { shouldShowModalForGroupMembership } from "features/Licensing/ContentAssignmentModalLicensingSteps/state/thunks/licensingModalThunk";
import packsDataService from "features/Licensing/Packs/services/packsDataService";
import { LICENCE_CONTENT } from "features/People/constants";
import { withLDConsumer } from "launchdarkly-react-client-sdk";
import { type WithId } from "types";
import { type FiltersMap } from "utils/filterUtils";
import {
  ContentAssignment,
  ContentAssignmentModal,
  DeleteLinkButton,
  EditPriorityLinkButton,
  GroupsAssignmentModal,
  RemoveLinkButton,
  RestrictedAddButton,
  RestrictedByTooltip,
  RestrictedResource,
  RolesAssignmentModal,
  Segments,
  type UserGroupsAssignment,
  type UserRolesAssignment,
} from "../../../../components";
import ChangePeopleContentPriorityModal from "../../../../components/assignmentModals/changePriorityModal/ChangePeopleContentPriorityModal";
import GenericSectionHeader from "../../../../components/genericSectionHeader/GenericSectionHeader";
import DeleteUsersConfirmationModal from "../../../../components/people/deleteUsersConfirmationModal/DeleteUsersConfirmationModal";
import RemovePriorityConfirmationModal from "../../../../components/people/removeContentConfirmationModal/RemovePriorityConfirmationModal";
import RemoveGroupsConfirmationModal from "../../../../components/people/removeGroupsConfirmationModal/RemoveGroupsConfirmationModal";
import RemoveRolesConfirmationModal from "../../../../components/people/removeRolesConfirmationModel/RemoveRolesConfirmationModal";
import {
  AddPeopleToContentTypes,
  AlertTypes,
  AssignmentPeopleContext,
  ContentTypesEnum,
  RolePermissions,
  RouteNames,
  SortingDirection,
  Strings,
  TemplateTypes,
  ViewType,
} from "../../../../enums";
import { bindAction, type ContentPriorityLevel, type NotifyStepSettings } from "../../../../interfaces";
import { groupListUtils, Observable, stringUtils, userListUtils, validationSchemas } from "../../../../utils";
import navigationUtils from "../../../../utils/navigationUtils";
import { type AppDispatch, type RootState } from "../../../Application/globaltypes/redux";
import Restricted from "../../../Application/Restricted";
import { UserFlowPriorityChangedSuccess } from "../../../Application/services/realTimeNotification/events/library/libraryEvents";
import {
  AddUserToGroupAllSuccess,
  AssignUsersToRoles,
  UnassignRolesSuccess,
  UserRemoveFromGroupSuccess,
} from "../../../Application/services/realTimeNotification/events/people/peopleEvents";
import RtnEventsEmitter from "../../../Application/services/realTimeNotification/rtnEventsEmitter";
import backgroundTask from "../../../BackgroundTasks/backgroundTask";
import * as backgroundTasksActionsRedux from "../../../BackgroundTasks/state/backgroundTasksActions";
import { type LDProps } from "../../../LDProps";
import { getFilterOptions as eventGetFilterOptions } from "../../../Library/Events/state/thunks/eventFiltersThunk";
import {
  editPeopleContentPriority,
  removeContentPrioritiesDistributed,
  unassignContentDistributed,
} from "../../../Library/PeopleAssignments/state/actions/commonActions";
import { ContentType, PeopleType } from "../../../Library/PeopleAssignments/types";
import {
  getUpdatedPriorityItems,
  mapUserAssignmentsToPriorityItems,
} from "../../../Library/PeopleAssignments/utils/peopleAssignmentUtils";
import { resetUserPacksGrid } from "../../../Licensing/Packs/state/slices/userPacksGridSlice";
import {
  issuePackLicenseToPeople,
  removePackLicenseFromPeople,
} from "../../../Licensing/Packs/state/thunks/packPurchasedViewThunk";
import { fetchUserPacksGrid } from "../../../Licensing/Packs/state/thunks/userPacksGridThunk";
import { type UserPacksRequest } from "../../../Licensing/Packs/types/requests";
import * as notificationsActionsRedux from "../../../Notifications/state/notificationsActions";
import * as groupsFilterActionsRedux from "../../Groups/GroupsOverview/state/filterActions";
import * as groupsOverviewActionsRedux from "../../Groups/GroupsOverview/state/groupsOverviewActions";
import * as rolesFilterActionsRedux from "../../RolesOverview/state/filterActionCreators";
import * as rolesOverviewActionsRedux from "../../RolesOverview/state/rolesOverviewActions";
import * as addToContentActionsRedux from "../../state/slices/addToContentSlice";
import { setSelected } from "../../state/slices/contentAssignmentSlice";
import { addUsersToGroups, deleteUsers } from "../../state/thunks/peopleThunk";
import {
  type ContentAssignmentModelItem,
  type IFlowAssignmentModelItem,
  type PriorityItem,
  type SelectedContentMap,
} from "../../types";
import { assignUsersToRoleTask } from "../backgroundTasks/assignUsersToRoleTask";
import UserPerformance from "../Performance/UserPerformance";
import * as userCommonActionsRedux from "../state/userCommonActions";
import UserGroupList from "../UserGroupList/UserGroupList";
import * as filterActionCreators from "../UsersOverview/state/filterActionCreators";
import * as usersOverviewActionsRedux from "../UsersOverview/state/usersOverviewActions";
import removeUserFromGroupsTask from "./backgroundTasks/removeUserFromGroupsTask";
import "./editUser.scss";
import EditUserInfo from "./EditUserInfo";
import { issueLicensePermissionPredicate } from "./issueLicensePermissionPredicate";
import UserRoleList from "./RoleListInfo";
import * as editUserActionsRedux from "./state/editUserActions";
import * as editUserGroupsActionsRedux from "./state/editUserGroupsActions";
import * as editUserGroupsFilterActionsRedux from "./state/editUserGroupsFilterActionCreators";
import * as editUserRolesActionsRedux from "./state/editUserRolesActions";
import * as filterActionsRedux from "./state/editUserRolesFilterActionCreators";
import UserPacks from "./UserPacks/UserPacks";

const groupsListViewRtnEvents = [AddUserToGroupAllSuccess, UserRemoveFromGroupSuccess];
const roleListViewRtnEvents = [AssignUsersToRoles, UnassignRolesSuccess];
const contentListViewRtnEvents = [UserFlowPriorityChangedSuccess];

const segmentPaths = {
  configure: "configure",
  roles: "roles",
  groups: "groups",
  content: "content",
  licenses: "licenses",
};

type PropsFromRedux = ConnectedProps<typeof connector>;

interface EditUserState {
  selectedGroups: SelectedItem[];
  selectedRoles: SelectedItem[];
  selectedPacks: SelectedItem[];
  viewType: ViewType;
  clickedUserGroupIdToRemove: number;
  clickedUserRoleIdToRemove: number;
  clickedUserPackIdToRemove: number;
  confirmation: {
    priorityItems: PriorityItem[];
    show: boolean;
    proceed?: () => Promise<boolean>;
  };
  addGroupsModal: {
    show: boolean;
    selectedGroupIds: number[];
    search: string;
  };
  showRemoveRoleUserConfirmation: boolean;
  showDeleteConfirmation: boolean;
  showRemoveGroupsConfirmation: boolean;
  showAddToRoleModal: boolean;
  showPeopleIssueLicenseModal: boolean;
  removeLicenseModal: {
    show: boolean;
    groupLicensesCount: number;
    userLicensesCount: number;
  };
}

export interface EditUserProps extends WithRouterProps {
  assetsFilterOptions: any;
}

type Props = EditUserProps & PropsFromRedux & FormikProps<any> & LDProps;

export class EditUser extends Component<Props, EditUserState> {
  private readonly flexiblePriorityDueDate: boolean;
  private readonly selectedContent: SelectedContentMap;
  constructor(props: Props) {
    super(props);

    this.state = {
      selectedGroups: [] as SelectedItem[],
      selectedRoles: [] as SelectedItem[],
      selectedPacks: [] as SelectedItem[],
      viewType: ViewType.LIST,
      clickedUserGroupIdToRemove: 0,
      clickedUserRoleIdToRemove: 0,
      clickedUserPackIdToRemove: 0,
      confirmation: {
        priorityItems: [],
        show: false,
      },
      addGroupsModal: {
        show: false,
        selectedGroupIds: [],
        search: "",
      },
      removeLicenseModal: {
        show: false,
        groupLicensesCount: 0,
        userLicensesCount: 0,
      },
      showRemoveRoleUserConfirmation: false,
      showDeleteConfirmation: false,
      showRemoveGroupsConfirmation: false,
      showAddToRoleModal: false,
      showPeopleIssueLicenseModal: false,
    };

    this.flexiblePriorityDueDate = !!this.props.flags?.[FeatureFlags.FlexiblePriorityDueDate];
    this.selectedContent = new Map();
  }

  userId = parseInt(this.props.params.id!);

  onChangePriorityObserver = new Observable();
  onClearPriorityObserver = new Observable();
  reloadGroupListItems?: (enableSorting: boolean) => void;

  showUserDeleteConfirmation = () => {
    this.setState({
      showDeleteConfirmation: true,
    });
  };

  closeDeleteConfirmation = () => {
    this.setState({
      showDeleteConfirmation: false,
    });
  };

  onDeleteUserConfirm = async (userId: number) => {
    this.closeDeleteConfirmation();
    this.props.navigate("/people/users");

    this.props.deleteUsers([userId]);
  };

  componentDidMount() {
    const {
      editUserActions: { fetchUserInfo },
      userCommonActions: { fetchCountries },
    } = this.props;

    fetchCountries();
    fetchUserInfo(this.userId);
    RtnEventsEmitter.subscribe(contentListViewRtnEvents, this.clearSelectedContent);
  }

  componentWillUnmount() {
    this.props.editUserActions.resetUserInfo();
    RtnEventsEmitter.unsubscribe(contentListViewRtnEvents, this.clearSelectedContent);
  }

  clearSelectedContent = () => {
    this.props.setSelectedContentIds({ context: ContentTypesEnum.Flows, selected: [] });
    this.selectedContent.clear();
  };

  addUserToRoleHeaderButton = (hasPermissions: boolean) => {
    return (
      <RestrictedAddButton
        buttonText="Add Role"
        onClick={() => this.onAddToRoleModal()}
        hasPermission={hasPermissions}
      />
    );
  };

  removeContentBtn = (ids: number[], unremovableSelected: boolean) => (
    <RemovePriorityButton
      onClick={() => this.onClearPriorityClick(ids)}
      isDisabled={unremovableSelected}
      context="people"
      entityType={ContentType.Flow}
      peopleType={PeopleType.User}
      showTooltip={unremovableSelected}
      multiple
    />
  );

  /* istanbul ignore next */
  onEditPriorityClick = (item?: IFlowAssignmentModelItem) => {
    const assignments = item ? [item] : [...this.selectedContent.values()];
    const priorityItems = mapUserAssignmentsToPriorityItems(
      assignments as ContentAssignmentModelItem[],
      ContentType.Flow,
      this.flexiblePriorityDueDate,
    );
    this.onChangePriorityObserver.notify(() => {}, { [PeopleType.User]: priorityItems });
  };

  /* istanbul ignore next */
  onClearPriorityClick = (ids: number[]) => {
    this.onClearPriorityObserver.notify(() => {
      const flowItems = { [ContentType.Flow]: ids };
      this.props.removeContentPriorities(this.userId, AssignmentPeopleContext.User, flowItems);

      this.clearSelectedContent();
    });
  };

  editPriorityBtn = () => {
    return <EditPriorityLinkButton onClick={() => this.onEditPriorityClick()} />;
  };

  onCancelAddToGroupModal = () => {
    this.props.resetLicensingModal();
    this.setState(() => ({
      selectedGroups: [],
      addGroupsModal: {
        selectedGroupIds: [],
        show: false,
        search: "",
      },
    }));
  };

  /* istanbul ignore next */
  openAddToGroupModal = () => {
    this.props.usersOverviewActions.fetchUserGroupsAssignments([this.userId]);
    this.setState((state) => ({
      addGroupsModal: {
        ...state.addGroupsModal,
        show: true,
      },
    }));
  };

  addUserToGroupHeaderButton = (hasPermission: boolean) => {
    return (
      <RestrictedAddButton buttonText="Add Group" onClick={this.openAddToGroupModal} hasPermission={hasPermission} />
    );
  };

  fetchRoles = (skip = 0, top = 10, orderProp = "", orderDirection = "", filter: any = "") => {
    this.props.rolesActions.fetchRoles(this.userId, skip, top, orderProp, orderDirection, filter);
  };

  hideContentCreate = () => !LICENCE_CONTENT.includes(this.props.selectedType);

  renderEditUserContent = () => {
    return (
      <div className="edit-user-content-step">
        <ContentAssignment
          entityId={this.userId}
          contentMap={this.selectedContent}
          contextMenuButtonHandlers={{
            onEditPriority: this.onEditPriorityClick,
            onClearPriority: this.onClearPriorityClick,
          }}
          disabled={!this.props.userInfo.canEdit}
          peopleType={PeopleType.User}
          onAddContent={() => this.props.onAddContent(this.userId)}
        />
      </div>
    );
  };

  renderEditUserLicenses = () => {
    const { userPacks, userPacksActions, accountId, userInfo } = this.props;

    const packsActions = {
      fetchPacksGrid: userPacksActions.fetchUserPacksGrid,
      resetPacksGrid: userPacksActions.resetUserPacksGrid,
    };

    return (
      <>
        <UserPacks
          userId={this.userId}
          accountId={accountId}
          packsGrid={userPacks.grid}
          packsActions={packsActions}
          issueLicenseButton={this.issueLicenseHeaderButton}
          sendTransientNotification={this.props.notificationsActions.sendTransientNotification}
          onRemoveLicenseClick={this.onRemoveLicenseClick}
          isReadOnly={!userInfo.canEdit}
          selectedPackIds={this.getSelectedPackIds()}
          onSelectedPacksChanged={this.onSelectedPacksChanged}
        />
        {this.state.showPeopleIssueLicenseModal && this.renderPeopleIssueLicenseModal()}
      </>
    );
  };

  saveUserInfo = () => {
    const {
      editUserActions: { updateUserInfo },
      values,
    } = this.props;

    /* istanbul ignore next */
    const coercedUserInfo = validationSchemas.getCreateUserSchema().cast(values);

    updateUserInfo(coercedUserInfo);

    const params = {
      id: "UpdateUserInformation",
      title: "Update user information",
      getMessageIdsV2: () => [`UserUpdated_${this.userId}`],
      callApi: () => dataService.updateUser(coercedUserInfo),
      successTransientMessage: "User information update succeeded!",
      failureTransientMessage: "User information update failed!",
    };

    const {
      backgroundTasksActions: { addOperationV2 },
      notificationsActions: { sendTransientNotification },
    } = this.props;

    backgroundTask.updateEntityV2(params, {
      addOperation: addOperationV2,
      sendTransientNotification,
    });
  };

  fetchGroups = (skip: number, top: number, orderByParams: string, filterParams: any) => {
    this.props.userGroupsActions.fetchUserGroups(this.userId, skip, top, orderByParams, filterParams);
  };

  onSelectedRolesChanged = (roleIds: number[]) => {
    const { selectedRoles } = this.state;
    const updatedSelection = selectedRoles.filter((g) => roleIds.includes(g.id));

    const roles = this.props.userRoles.items;
    const addedRoles = roles
      .filter((g) => roleIds.includes(g.id) && selectedRoles.every((sg) => sg.id !== g.id))
      .map((r) => ({ id: r.id, title: r.name }));

    this.setState({
      selectedRoles: [...updatedSelection, ...(addedRoles as SelectedItem[])],
    });
  };

  onSelectedGroupsChanged = (groupIds: number[]) => {
    const { selectedGroups } = this.state;
    const updatedSelection = selectedGroups.filter((g) => groupIds.includes(g.id));

    const groups = this.props.userGroups.list.items;

    const addedGroups = groups.filter(
      (g: WithId) => groupIds.includes(g.id) && selectedGroups.every((sg) => sg.id !== g.id),
    );

    this.setState({
      selectedGroups: [...updatedSelection, ...addedGroups],
    });
  };

  /* istanbul ignore next */
  onSelectedPacksChanged = (packIds: number[]) => {
    const { selectedPacks } = this.state;
    const updatedSelection = selectedPacks.filter((p) => packIds.includes(p.id));

    const packs = this.props.userPacks.grid.items;
    const addedPacks = packs.filter((p) => packIds.includes(p.id) && selectedPacks.every((sp) => sp.id !== p.id));

    this.setState({
      selectedPacks: [...updatedSelection, ...addedPacks],
    });
  };

  closeRemoveRoleConfirmation = () => {
    this.setState({
      showRemoveRoleUserConfirmation: false,
      selectedRoles: [],
      clickedUserRoleIdToRemove: 0,
    });
  };

  rolesToRemove = () => {
    const { selectedRoles, clickedUserRoleIdToRemove } = this.state;
    return clickedUserRoleIdToRemove > 0 ? [clickedUserRoleIdToRemove] : selectedRoles.map((x) => x.id);
  };

  onRemoveUserRole = async () => {
    const userId = this.userId;
    const rolesToRemove = this.rolesToRemove();

    if (isNumber(userId) && some(rolesToRemove)) {
      const roleInMessageSingular = rolesToRemove.length === 1;
      const params = {
        id: "UnassignUserFromRoles",
        title: "Unassign user from roles",
        getMessageIds: async () => {
          const data = await rolesDataService.unassignRolesFromUsers([userId], rolesToRemove);
          return [data];
        },
        successTransientMessage: roleInMessageSingular
          ? "User has been unassigned from role successfully"
          : "User have been unassigned from roles successfully",
        failureTransientMessage: roleInMessageSingular
          ? "Unassigning user from role failed!"
          : "Unassigning user from roles failed!",
      };

      const {
        backgroundTasksActions: { addOperationV1 },
        notificationsActions: { sendTransientNotification },
      } = this.props;

      await backgroundTask.updateEntity(params, {
        addOperation: addOperationV1,
        sendTransientNotification,
      });
    }

    this.closeRemoveRoleConfirmation();
  };

  renderRemoveRoleUsersModal = () => {
    const { selectedRoles, clickedUserRoleIdToRemove } = this.state;

    const rolesCount = clickedUserRoleIdToRemove > 0 ? 1 : selectedRoles.length;

    return (
      <RemoveRolesConfirmationModal
        open={this.state.showRemoveRoleUserConfirmation}
        rolesCount={rolesCount}
        onCancel={this.closeRemoveRoleConfirmation}
        onContinue={this.onRemoveUserRole}
      />
    );
  };

  renderRemoveContentUsersModal = () => (
    <RemovePriorityConfirmationModal peopleType={PeopleType.User} removeObserver={this.onClearPriorityObserver} />
  );

  /* istanbul ignore next */
  onRemoveLicenseClick = async (packId: number = 0) => {
    const { selectedPacks } = this.state;

    const packsCount = packId > 0 ? 1 : selectedPacks.length;
    const packIds = packId > 0 ? [packId] : this.getSelectedPackIds();

    const userGroupLicenses = await packsDataService.getUserGroupLicenses([this.userId], packIds);
    const groupLicensesCount = uniq(userGroupLicenses.flatMap((i) => i.groupIds)).length;

    this.setState({
      removeLicenseModal: {
        show: true,
        groupLicensesCount,
        userLicensesCount: packsCount,
      },
      clickedUserPackIdToRemove: packId,
    });
  };

  closeRemoveLicenseConfirmation = () => {
    this.setState({
      removeLicenseModal: {
        show: false,
        groupLicensesCount: 0,
        userLicensesCount: 0,
      },
      selectedPacks: [],
      clickedUserPackIdToRemove: 0,
    });
  };

  packsToRemove = () => {
    const { selectedPacks, clickedUserPackIdToRemove } = this.state;
    return clickedUserPackIdToRemove > 0 ? [clickedUserPackIdToRemove] : selectedPacks.map((x) => x.id);
  };

  onRemoveUserLicense = () => {
    const packsToRemove = this.packsToRemove();

    const licenses = packsToRemove.map((packId) =>
      userListUtils.mapToAssignmentItem(this.userId, { id: packId }, "pack", AssignmentPeopleContext.User),
    );

    this.props.removePackLicense({
      peopleType: AssignmentPeopleContext.User,
      items: licenses,
    });

    this.closeRemoveLicenseConfirmation();
  };

  renderRemoveUserLicenseModal = () => {
    const { removeLicenseModal } = this.state;
    return (
      <RemoveLicenseConfirmationDialog
        groupLicensesCount={removeLicenseModal.groupLicensesCount}
        userLicensesCount={removeLicenseModal.userLicensesCount}
        triggerOpen={removeLicenseModal.show}
        onCancel={this.closeRemoveLicenseConfirmation}
        onContinue={this.onRemoveUserLicense}
        isPackContext={false}
      />
    );
  };

  onAddToRoleModal = () => {
    this.props.usersOverviewActions.fetchUserRolesAssignments([this.userId]);
    this.setState({
      showAddToRoleModal: true,
    });
  };

  onCloseAddToRoleModal = () => {
    this.setState({
      showAddToRoleModal: false,
    });
  };

  onAddToRolesConfirm = async (roleIds: number[]) => {
    this.onCloseAddToRoleModal();

    const {
      backgroundTasksActions: { addOperationV1 },
      notificationsActions: { sendTransientNotification },
    } = this.props;

    const params = assignUsersToRoleTask([this.userId], roleIds);

    await backgroundTask.updateEntity(params!, {
      addOperation: addOperationV1,
      sendTransientNotification,
    });
  };

  renderAddUserToRoleModal = () => {
    const {
      rolesFilterOptions,
      accountId,
      rolesFilterActions,
      rolesToAdd,
      userRolesAssignments,
      rolesOverviewActions: { fetchRoles },
    } = this.props;

    const { showAddToRoleModal } = this.state;

    return (
      showAddToRoleModal && (
        <RolesAssignmentModal
          dataLoader={fetchRoles}
          accountId={accountId}
          rolesCount={rolesToAdd.itemsCount}
          isListLoading={rolesToAdd.isLoading || userRolesAssignments.isLoading}
          roles={rolesToAdd.items}
          filterActions={rolesFilterActions}
          filterOptions={rolesFilterOptions}
          selectedUserIds={[this.userId]}
          onConfirm={this.onAddToRolesConfirm}
          showModal={showAddToRoleModal}
          onCancel={this.onCloseAddToRoleModal}
          userRolesAssignments={userRolesAssignments.items as UserRolesAssignment[]}
        />
      )
    );
  };

  renderDropdownButtons = () => {
    const { selectedGroups, selectedRoles, selectedPacks } = this.state;
    return some(selectedGroups) || some(selectedRoles) || some(selectedPacks)
      ? null
      : [<DeleteLinkButton key="delete-user" onClick={this.showUserDeleteConfirmation} />];
  };

  onGroupsSelected = (groupIds: number[]) => {
    const { shouldShowLicensingModal } = this.props;
    this.setState((state) => ({
      addGroupsModal: {
        ...state.addGroupsModal,
        selectedGroupIds: groupIds,
      },
    }));

    return shouldShowLicensingModal(groupIds, [this.userId]);
  };

  onConfirmAddToGroupModal = async (notifyStepSettings?: NotifyStepSettings) => {
    const { addGroupsModal } = this.state;

    this.setState(() => ({
      addGroupsModal: {
        show: false,
        selectedGroupIds: [],
        search: "",
      },
    }));

    if (isEmpty(addGroupsModal.selectedGroupIds)) {
      return;
    }

    this.props.addUsersToGroups([this.userId], addGroupsModal.selectedGroupIds, notifyStepSettings);
  };

  handleAddToGroupSearchChange = (search: string) => {
    this.setState(
      (state) => ({
        addGroupsModal: {
          ...state.addGroupsModal,
          search,
        },
      }),
      () => {
        this.reloadGroupListItems?.(search === "");
      },
    );
  };

  renderAddToGroupModal = () => {
    const {
      accountId,
      groupsFilterOptions,
      groupsFilterActions,
      groupsToAdd,
      userGroupsAssignments,
      groupsOverviewActions,
      isShouldShowModalLoading,
    } = this.props;

    const { addGroupsModal } = this.state;

    return (
      <GroupsAssignmentModal
        accountId={accountId}
        showModal={addGroupsModal.show}
        fetchGroups={groupsOverviewActions.fetchGroups}
        groups={groupsToAdd.items}
        groupsCount={groupsToAdd.itemsCount}
        isGroupsLoading={groupsToAdd.isLoading || userGroupsAssignments.isLoading}
        userGroupsAssignments={userGroupsAssignments.items as UserGroupsAssignment[]}
        fetchFilterOptions={groupsFilterActions.fetchFilterOptions}
        filterOptions={groupsFilterOptions}
        selectedUserIds={[this.userId]}
        onGroupsSelected={this.onGroupsSelected}
        onConfirm={this.onConfirmAddToGroupModal}
        onCancel={this.onCancelAddToGroupModal}
        notifyTemplateType={TemplateTypes.AddedToGroup}
        isStepLoading={isShouldShowModalLoading}
        search={this.state.addGroupsModal.search}
        onSearchChanged={this.handleAddToGroupSearchChange}
        setReloadListItems={(reloadListItems) => {
          this.reloadGroupListItems = reloadListItems;
        }}
      />
    );
  };

  renderChangePriorityModal = () => (
    <ChangePeopleContentPriorityModal
      onSave={(assignments: { [key in PeopleType]?: ContentPriorityLevel[] }) => {
        getUpdatedPriorityItems(assignments, (items: ContentPriorityLevel[]) => {
          this.props.editPriority(items, this.userId, this.flexiblePriorityDueDate);
        });
        this.clearSelectedContent();
      }}
      onTriggerModalObserver={this.onChangePriorityObserver}
    />
  );

  onAddPacksToUserModal = () => {
    this.setState({
      showPeopleIssueLicenseModal: true,
    });
  };

  onCancelPeopleIssueLicenseModal = () => {
    this.hideIssueLicenseModal();
  };

  hideIssueLicenseModal = () => {
    this.setState({
      showPeopleIssueLicenseModal: false,
    });
  };

  requestLicenseIssue = (selectedPackIds: number[]) => {
    if (some(selectedPackIds)) {
      const assignments = selectedPackIds.map((packId) =>
        userListUtils.mapToAssignmentItem(this.userId, { id: packId }, "pack", AssignmentPeopleContext.User),
      );
      this.props.issuePackLicense({
        peopleType: AssignmentPeopleContext.User,
        items: assignments,
      });
    }

    this.hideIssueLicenseModal();

    return Promise.resolve();
  };

  renderPeopleIssueLicenseModal = () => {
    const { accountId } = this.props;

    return (
      <EditUserPacksIssueLicenseModalContainer
        showModal={this.state.showPeopleIssueLicenseModal}
        onCancel={this.onCancelPeopleIssueLicenseModal}
        accountId={accountId}
        onConfirm={this.requestLicenseIssue}
        peopleId={this.userId}
        assignmentPeopleContext={AssignmentPeopleContext.User}
      />
    );
  };

  issueLicenseHeaderButton = (
    <Restricted
      permissions={[]}
      permissionPredicate={issueLicensePermissionPredicate}
      renderContent={(hasAnyPermission) => (
        <RestrictedByTooltip hasPermission={hasAnyPermission}>
          <Button
            primary
            className="create-button"
            content="Issue License"
            onClick={this.onAddPacksToUserModal}
            disabled={!hasAnyPermission}
          />
        </RestrictedByTooltip>
      )}
    />
  );

  loadUserGroups = (
    skip = 0,
    top = 10,
    sortingColumnName = "",
    sortingDirection: SortingDirection = SortingDirection.Ascending,
    filter?: FiltersMap,
  ) => {
    let orderParams = "";
    let filterParams = {};

    if (sortingColumnName) {
      orderParams = groupListUtils.formatOrderParams(sortingColumnName, sortingDirection);
    }

    if (filter) {
      filterParams = groupListUtils.formatFilterParams(filter);
    }

    return this.fetchGroups(skip, top, orderParams, filterParams);
  };

  groupsToRemove = () => {
    const { selectedGroups, clickedUserGroupIdToRemove } = this.state;
    return clickedUserGroupIdToRemove > 0 ? [clickedUserGroupIdToRemove] : selectedGroups.map((x) => x.id);
  };

  onRemoveUserGroups = async () => {
    const userId = this.userId;
    const groupsToRemove = this.groupsToRemove();

    if (isNumber(userId) && some(groupsToRemove)) {
      const params = {
        task: { ...removeUserFromGroupsTask.InProgress(`RemoveUserFromGroups_${Date.now()}`), indeterminate: false },
        getMessageIdsV2: () => groupsToRemove.map((groupId) => `RemoveUserFromGroup_${userId}:${groupId}`),
        callApi: () => groupsDataService.removeUserFromGroups(userId, groupsToRemove),
        onCompletedInTime: ({ isSuccessful }: { isSuccessful: boolean }) => {
          let message = {
            type: AlertTypes.error,
            message: `User removal from ${stringUtils.pluralize("group", groupsToRemove.length)} failed!`,
          };

          if (isSuccessful) {
            message = {
              type: AlertTypes.success,
              message: `User has been removed from ${stringUtils.pluralize("group", groupsToRemove.length)}!`,
            };
          }

          this.props.notificationsActions.sendTransientNotification(message);
        },
      };
      this.props.backgroundTasksActions.addOperationV2(params);
    }

    this.setState({
      showRemoveGroupsConfirmation: false,
      selectedGroups: [],
      clickedUserGroupIdToRemove: 0,
    });
  };

  renderModals = () => {
    const { selectedGroups, clickedUserGroupIdToRemove } = this.state;

    const groupsCount = clickedUserGroupIdToRemove > 0 ? 1 : selectedGroups.length;

    return (
      <>
        <RemoveGroupsConfirmationModal
          open={this.state.showRemoveGroupsConfirmation}
          groupsCount={groupsCount}
          onCancel={this.hideRemoveGroupModal}
          onContinue={this.onRemoveUserGroups}
        />
      </>
    );
  };

  renderAddToContentModal = () =>
    this.props.showAddToContentModal && <ContentAssignmentModal peopleType={PeopleType.User} />;

  removeGroupsBtn = (<RemoveLinkButton className="remove-group-button" onClick={() => this.showRemoveGroupModal()} />);

  removeRolesBtn = (<RemoveLinkButton className="remove-role-button" onClick={() => this.showRemoveRoleModal()} />);

  removePacksBtn = (
    <Restricted
      permissions={[]}
      permissionPredicate={issueLicensePermissionPredicate}
      renderContent={(hasAnyPermission) => (
        <RestrictedByTooltip hasPermission={hasAnyPermission}>
          <RemoveLinkButton
            className="remove-pack-button"
            onClick={() => this.onRemoveLicenseClick()}
            isDisabled={!hasAnyPermission}
          />
        </RestrictedByTooltip>
      )}
    />
  );

  addContentBtn = () =>
    this.hideContentCreate() ? undefined : (
      <Button
        className="add-content create-button"
        primary
        onClick={() => {
          this.props.onAddContent(this.userId);
        }}
      >
        {Strings.modalTitles.addContent}
      </Button>
    );

  showRemoveRoleModal = (roleId = 0) => {
    this.setState({
      showRemoveRoleUserConfirmation: true,
      clickedUserRoleIdToRemove: roleId,
    });
  };

  showRemoveGroupModal = (groupId = 0) => {
    this.setState({
      showRemoveGroupsConfirmation: true,
      clickedUserGroupIdToRemove: groupId,
    });
  };

  hideRemoveGroupModal = () => {
    this.setState({
      selectedGroups: [],
      showRemoveGroupsConfirmation: false,
    });
  };

  isPerformanceTabOpened = () => {
    const {
      location: { pathname },
    } = this.props;
    const currentPath = pathname.split("/").at(-1);
    return currentPath === this.userId.toString() || currentPath === "";
  };

  renderHeaderButtons = () => {
    const {
      location: { pathname },
    } = this.props;

    const { selectedGroups, selectedRoles, selectedPacks } = this.state;

    switch (pathname.split("/").at(-1)) {
      case segmentPaths.roles:
        if (some(selectedRoles)) {
          return [this.removeRolesBtn];
        }
        return [this.addUserToRoleHeaderButton(true)];
      case segmentPaths.groups:
        if (some(selectedGroups)) {
          return [this.removeGroupsBtn];
        }
        return [this.addUserToGroupHeaderButton(true)];
      case segmentPaths.content:
        if (!this.props.selectedFlowIds?.length) {
          return [this.addContentBtn()];
        }
        const assignments = [...this.selectedContent.values()];
        const unremovableFlowSelected = assignments.some((f) => !(f as IFlowAssignmentModelItem).canRemove);
        return [this.editPriorityBtn(), this.removeContentBtn(this.props.selectedFlowIds, unremovableFlowSelected)];
      case segmentPaths.licenses:
        if (some(selectedPacks)) {
          return [this.removePacksBtn];
        }
        return [this.issueLicenseHeaderButton];
      default:
        return null;
    }
  };

  getSelectedGroupIds = () => {
    const { selectedGroups } = this.state;
    return selectedGroups.map((x) => x.id);
  };

  getSelectedRoleIds = () => {
    const { selectedRoles } = this.state;
    return selectedRoles.map((x) => x.id);
  };

  getSelectedPackIds = () => {
    const { selectedPacks } = this.state;
    return selectedPacks.map((x) => x.id);
  };

  render() {
    const {
      errors,
      touched,
      isValid,
      handleChange,
      handleBlur,
      setFieldValue,
      setFieldTouched,
      dirty,
      values,
      resetForm,
      initialValues,

      params,
      accountId,
      countries,
      departments,
      jobTitles,
      filterOptionActions,
      userInfo,
      userRoles,
      filterActions,
      userPermissions,
      userGroups,
      userGroupsFilterActions,
      location,
      navigate,
    } = this.props;

    const validatedFieldProps = {
      errors,
      touched,
      isValid,
      handleChange,
      handleBlur,
      setFieldValue,
      setFieldTouched,
      dirty,
      values,
      resetForm,
      initialValues,
    };

    return (
      <RestrictedResource isAuthorized={!!userInfo.isAccessAuthorized}>
        <section className="edit-user nested-content">
          <GenericSectionHeader
            title={userInfo.isLoading ? "" : `${userInfo.firstName} ${userInfo.lastName}`}
            titleForGA="User Details"
            goBackAction={() => navigationUtils.goBackOrDefault(location, navigate, `/${RouteNames.peopleUsers}`)}
            buttons={this.renderHeaderButtons()}
            dropdownButtons={this.renderDropdownButtons()}
            isReadOnly={userInfo.canEdit === false}
            showReportLastUpdated={this.isPerformanceTabOpened()}
          />
          <DeleteUsersConfirmationModal
            selectedUsers={[this.userId]}
            isOpen={this.state.showDeleteConfirmation}
            onCancel={this.closeDeleteConfirmation}
            onContinue={() => {
              this.onDeleteUserConfirm(this.userId);
            }}
          />

          {this.renderModals()}
          {this.renderAddToContentModal()}
          {this.renderRemoveContentUsersModal()}
          {this.renderRemoveRoleUsersModal()}
          {this.renderAddUserToRoleModal()}
          {this.renderAddToGroupModal()}
          {this.renderChangePriorityModal()}
          {this.renderRemoveUserLicenseModal()}

          <Segments to={`/people/users/${params.id}`}>
            <Segments.Segment label="Performance" />
            <Segments.Segment to={segmentPaths.configure} label="Configure" />
            <Segments.Segment to={segmentPaths.roles} label="Roles" />
            <Segments.Segment to={segmentPaths.groups} label="Groups" />
            <Segments.Segment to={segmentPaths.content} label="Content" />
            <Segments.Segment to={segmentPaths.licenses} label="Licenses" />
          </Segments>
          <Routes>
            <Route path={"/"} element={<UserPerformance userId={this.userId} />} />
            <Route
              path={segmentPaths.configure}
              element={
                <EditUserInfo
                  {...validatedFieldProps}
                  accountId={accountId}
                  countries={countries.items}
                  departments={departments}
                  jobTitles={jobTitles}
                  filterOptionActions={filterOptionActions}
                  saveUserInfo={this.saveUserInfo}
                  isLoadingUserInfo={userInfo.isLoading}
                  isReadOnly={userInfo.canEdit === false}
                />
              }
            />
            <Route
              path={segmentPaths.roles}
              element={
                <UserRoleList
                  createRoleButton={this.addUserToRoleHeaderButton(
                    difference([RolePermissions.UsersManage, RolePermissions.RolesView], userPermissions).length === 0,
                  )}
                  dataLoader={this.fetchRoles}
                  roles={userRoles.items}
                  rolesCount={userRoles.itemsCount}
                  isLoading={userRoles.isLoading}
                  onSelectedRolesChanged={this.onSelectedRolesChanged}
                  filterOptions={userRoles.filterOptions}
                  appliedFilter={userRoles.appliedFilter}
                  filterActions={filterActions}
                  selectedRoleIds={this.getSelectedRoleIds()}
                  onRemoveMemberClick={(id: number) => this.showRemoveRoleModal(id)}
                  readOnly={userInfo.canEdit === false}
                  listViewRtnEvents={roleListViewRtnEvents}
                />
              }
            />

            <Route
              path={segmentPaths.groups}
              element={
                <UserGroupList
                  addGroupButton={this.addUserToGroupHeaderButton(
                    difference([RolePermissions.UsersManage, RolePermissions.GroupsView], userPermissions).length === 0,
                  )}
                  groups={userGroups.list.items}
                  groupsCount={userGroups.list.itemsCount}
                  loadPage={this.loadUserGroups}
                  selectedItemIds={this.getSelectedGroupIds()}
                  updateSelectedItems={this.onSelectedGroupsChanged}
                  isLoading={userGroups.list.isLoading}
                  appliedUserGroupFilter={userGroups.appliedFilter}
                  filterOptions={userGroups.filterOptions}
                  setUserGroupFilter={userGroupsFilterActions.setGroupsFilter}
                  resetUserGroupFilter={userGroupsFilterActions.resetGroupsFilter}
                  getFilterOptions={userGroupsFilterActions.fetchFilterOptions}
                  onRemoveUserFromGroup={(id: number) => this.showRemoveGroupModal(id)}
                  readOnly={userInfo.canEdit === false}
                  listViewRtnEvents={groupsListViewRtnEvents}
                />
              }
            />
            <Route path={segmentPaths.content} element={this.renderEditUserContent()} />
            <Route path={segmentPaths.licenses} element={this.renderEditUserLicenses()} />
            <Route path="*" element={<Navigate to="../" replace />} />
          </Routes>
        </section>
      </RestrictedResource>
    );
  }
}

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => {
  const { editUser, userCommon, usersOverview, groupsOverview, rolesOverview, addToContentModal } = state.people;
  const { userInfo, userRoles, userGroups, userPacks } = editUser;

  return {
    accountId: state.userProfile.accountId,
    backgroundTasks: state.backgroundTasks.tasks,
    groupsToAdd: groupsOverview.groupsList,
    groupsFilterOptions: groupsOverview.filterOptions,
    userGroupsAssignments: usersOverview.userGroupsAssignments,
    userRolesAssignments: usersOverview.userRolesAssignments,
    userContentAssignment: state.people.contentAssignment,
    userInfo: {
      id: userInfo.info?.id,
      firstName: userInfo.info?.firstName,
      lastName: userInfo.info?.lastName,
      country: userInfo.info?.country,
      officeLocation: userInfo.info?.officeLocation,
      department: userInfo.info?.department,
      jobTitle: userInfo.info?.jobTitle,
      email: userInfo.info?.email,
      isLoading: userInfo.isLoading,
      canEdit: userInfo.info?.canEdit,
      isBypassSso: userInfo.info?.isBypassSso,
      isAccessAuthorized: userInfo.isAccessAuthorized,
    },
    userRoles: {
      items: userRoles.rolesList.items,
      itemsCount: userRoles.rolesList.itemsCount,
      isLoading: userRoles.rolesList.isLoading,
      filterOptions: userRoles.filterOptions,
      appliedFilter: userRoles.filterState.appliedFilter,
    },
    userGroups: {
      list: userGroups.groupsList,
      grid: userGroups.groupsGrid,
      filterOptions: userGroups.filterOptions,
      appliedFilter: userGroups.filterState.appliedFilter,
    },
    countries: userCommon.countriesList,
    departments: state.people.usersOverview.filterOptions.departments,
    jobTitles: state.people.usersOverview.filterOptions.jobTitles,
    rolesFilterOptions: rolesOverview.filterOptions,
    rolesToAdd: rolesOverview.overviewList,
    showAddToContentModal: addToContentModal.showModal,
    userPacks: {
      grid: userPacks.packsGrid,
    },
    userPermissions: state.userProfile.permissions,
    isShouldShowModalLoading: state.licensing.licensingModal.isLoading,
    selectedFlowIds: state.people.contentAssignment.content.selected.Flows,
    selectedType: state.people.contentAssignment.content.selectedType,
  };
};

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: AppDispatch) => {
  return {
    contentAssignmentActions: bindActionCreators(() => {}, dispatch),
    backgroundTasksActions: bindActionCreators(backgroundTasksActionsRedux, dispatch),
    editUserActions: bindActionCreators(editUserActionsRedux, dispatch),
    filterActions: bindActionCreators(filterActionsRedux, dispatch),
    rolesActions: bindActionCreators(editUserRolesActionsRedux, dispatch),
    rolesOverviewActions: bindActionCreators(rolesOverviewActionsRedux, dispatch),
    rolesFilterActions: bindActionCreators(rolesFilterActionsRedux, dispatch),
    notificationsActions: bindActionCreators(notificationsActionsRedux, dispatch),
    userCommonActions: bindActionCreators(userCommonActionsRedux, dispatch),
    usersOverviewActions: bindActionCreators(usersOverviewActionsRedux, dispatch),
    filterOptionActions: bindActionCreators(filterActionCreators, dispatch),
    userGroupsActions: bindActionCreators(editUserGroupsActionsRedux, dispatch),
    groupsOverviewActions: bindActionCreators(groupsOverviewActionsRedux, dispatch),
    groupsFilterActions: bindActionCreators(groupsFilterActionsRedux, dispatch),
    userGroupsFilterActions: bindActionCreators(editUserGroupsFilterActionsRedux, dispatch),
    eventsGetFilterAction: bindActionCreators(eventGetFilterOptions, dispatch),
    addToContentActions: bindActionCreators(addToContentActionsRedux, dispatch),
    onAddContent: (userId: number) => {
      dispatch(addToContentActionsRedux.setSelectedContentType(AddPeopleToContentTypes.Flows));
      dispatch(addToContentActionsRedux.setSelectedPeopleIds([userId]));
      dispatch(addToContentActionsRedux.setSelectedPeopleType(AssignmentPeopleContext.User));
    },
    userPacksActions: {
      resetUserPacksGrid: () => dispatch(resetUserPacksGrid()),
      fetchUserPacksGrid: (requestData: UserPacksRequest) => dispatch(fetchUserPacksGrid(requestData)),
    },
    editPriority: (priorityLevels: ContentPriorityLevel[], userId: number, useFlexibleDueDate: boolean) =>
      dispatch(editPeopleContentPriority(priorityLevels, userId, PeopleType.User, useFlexibleDueDate)),
    unassignContent: bindAction(unassignContentDistributed, dispatch),
    removeContentPriorities: bindAction(removeContentPrioritiesDistributed, dispatch),
    issuePackLicense: bindActionCreators(issuePackLicenseToPeople, dispatch),
    removePackLicense: bindActionCreators(removePackLicenseFromPeople, dispatch),
    addUsersToGroups: bindActionCreators(addUsersToGroups, dispatch),
    shouldShowLicensingModal: bindAction(shouldShowModalForGroupMembership, dispatch),
    resetLicensingModal: bindAction(reset, dispatch),
    deleteUsers: bindActionCreators(deleteUsers, dispatch),
    setSelectedContentIds: bindAction(setSelected, dispatch),
  };
};

const connector = connect(mapStateToProps, mapDispatchToProps);

/* istanbul ignore next */
const component = withFormik({
  validationSchema: (props: any) => {
    return validationSchemas.getEditUserSchema(props.userInfo.id, props.userInfo.email);
  },
  mapPropsToValues: (props: any) => {
    return { ...props.userInfo };
  },
  enableReinitialize: true,
  handleSubmit: () => {
    // handler is required in order for submitForm`s returned promise to resolve
  },
})(EditUser);

const ConnectedComponent = connector(withRouter(withLDConsumer()(component)));
export default ConnectedComponent;
